import createContext from 'contexts/create-context';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { AIDraftAction, IAIDraftState, aiDraftReducer, initialAIDraftState } from './reducer';
import { pollExtractLanes } from '../../models';
import type { ExtractedLane } from '../../endpoints/ExtractLanesEndpoint.types';
import { mapExtractedLaneToValues } from '../types';

interface ProviderValue {
  state: IAIDraftState;
  dispatch: React.Dispatch<AIDraftAction>;
}

const [useContext, ReactProvider] = createContext<ProviderValue>();

export const AIDraftProvider: React.FC<{ children?: React.ReactNode }> = (props) => {
  const { children } = props;
  const [state, dispatch] = React.useReducer(aiDraftReducer, initialAIDraftState);
  const [searchParams] = useSearchParams();
  const restoredRef = React.useRef(false);

  React.useEffect(() => {
    if (restoredRef.current) return;
    const draftId = searchParams.get('draftId');
    if (!draftId) return;

    restoredRef.current = true;

    pollExtractLanes(draftId).then((result) => {
      if (result.status === 'completed' && result.lanes) {
        const lanes = (result.lanes as ExtractedLane[]).map(mapExtractedLaneToValues);
        dispatch({
          type: 'RESTORE_DRAFT',
          draftId,
          draftName: '',
          lanes,
          activeWindow: 'REVIEW_LANES',
        });
      } else if (result.status === 'processing') {
        dispatch({
          type: 'RESTORE_DRAFT',
          draftId,
          draftName: '',
          activeWindow: 'PROCESSING',
        });
      } else if (result.status === 'failed') {
        dispatch({
          type: 'RESTORE_DRAFT',
          draftId,
          draftName: '',
          activeWindow: 'PROCESSING',
        });
        dispatch({ type: 'EXTRACTION_ERROR', error: result.error ?? 'Extraction failed' });
      }
    });
  }, [searchParams]);

  return <ReactProvider value={{ state, dispatch }}>{children}</ReactProvider>;
};

export const useAIDraftCtx = useContext;
